// A labelled entry field (DESIGN-SPEC §6.2): a visible 13/20 label 6 px above
// the control, and one message slot below it where an error replaces the
// helper text. The control receives its id, aria-invalid and aria-describedby.
import { useId, type ReactNode } from 'react';
import { CircleAlertIcon } from 'lucide-react';

export interface EntryControlProps {
  id: string;
  'aria-invalid': true | undefined;
  'aria-describedby': string | undefined;
}

export function EntryField({
  label,
  help,
  error,
  aside,
  children,
}: {
  label: string;
  help?: string | null;
  error?: string | null;
  /** A quiet action on the label row (for example "Forgot password?"). */
  aside?: ReactNode;
  children: (control: EntryControlProps) => ReactNode;
}) {
  const id = useId();
  const messageId = `${id}-message`;
  const message = error || help || null;
  return (
    <div className="pc-field">
      <div className="pc-field-label-row">
        <label className="pc-field-label" htmlFor={id}>
          {label}
        </label>
        {aside}
      </div>
      {children({
        id,
        'aria-invalid': error ? true : undefined,
        'aria-describedby': message ? messageId : undefined,
      })}
      {message ? (
        <p id={messageId} className="pc-field-message" data-tone={error ? 'error' : undefined}>
          {message}
        </p>
      ) : null}
    </div>
  );
}

/** Form-level outcome from the server (wrong credentials, rate limit, outage). */
export function EntryAlert({ children }: { children: ReactNode }) {
  return (
    <p role="alert" className="pc-entry-alert">
      <CircleAlertIcon aria-hidden="true" />
      <span>{children}</span>
    </p>
  );
}
